import { z } from 'zod'

// Access token
export const compsInsightTokenSchema = z.object({
  access_token: z.string(),
})
export type CompsToken = z.infer<typeof compsInsightTokenSchema>

// Comps request body
export const compsInsightRequestSchema = z.object({
  // Market/Submarket combo
  market: z.string().optional(),
  submarket: z.string().optional(),
  // An array of geojson polygons that have been toStringed
  polygons: z.optional(z.array(z.string())),
  format: z.optional(z.literal('GEOJSON')),
  lastNYears: z.optional(z.number().gte(1).lte(10)),
  spaceTypes: z.array(z.literal('Retail')),
  floorsCount: z.optional(z.number().gt(0)),
  buildingClass: z.optional(z.literal('A').or(z.literal('B')).or(z.literal('C'))),
})
export type CompsInsightsRequest = z.infer<typeof compsInsightRequestSchema>

// Comps response body
const dateValueListSchema = z.array(
  z.object({
    date: z.string(),
    value: z.number(),
  })
)
const resultsSchema = z.object({
  transactionSize: dateValueListSchema,
  freeMonths: dateValueListSchema,
  startingRent: dateValueListSchema,
  effectiveRent: dateValueListSchema,
  workValue: dateValueListSchema,
  leaseTerm: dateValueListSchema,
})

const distributionValueSchema = z.object({
  min: z.number(),
  max: z.number(),
  median: z.number(),
  std: z.number(),
  histogram: z.object({
    x: z.array(z.number()),
    y: z.array(z.number()),
  }),
  kde: z.object({
    x: z.array(z.number()),
    y: z.array(z.number()),
  }),
  percentile25: z.number(),
  percentile75: z.number(),
})
const distributionSchema = z.object({
  transactionSize: distributionValueSchema,
  freeMonths: distributionValueSchema,
  startingRent: distributionValueSchema,
  effectiveRent: distributionValueSchema,
  workValue: distributionValueSchema,
  leaseTerm: distributionValueSchema,
})

// Market and Submarket response
export const compsInsightsResponseSchema = z.object({
  market: z.string(),
  submarket: z.string(),
  marketResults: resultsSchema,
  submarketResults: resultsSchema,
  marketDistributions: distributionSchema,
  submarketDistributions: distributionSchema,
  error: z.string().optional(),
})
export type CompsInsightsResponse = z.infer<typeof compsInsightsResponseSchema>

// Submarket Mappings
const compsSubmarketMappings = z.object({
  market: z.string().nonempty(),
  submarket: z.string().nonempty(),
  submarketId: z.string().nonempty(),
  shape: z
    .object({
      type: z.literal('MultiPolygon'),
      coordinates: z.array(z.array(z.array(z.array(z.number())))),
    })
    .or(
      z.object({
        type: z.literal('Polygon'),
        coordinates: z.array(z.array(z.array(z.number()))),
      })
    ),
})
export type CompsSubmarketMappings = z.infer<typeof compsSubmarketMappings>
